import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { useFnol, useMyOpenClaims } from "./use-claims";
import type { Claim, ClaimStatus } from "./types";

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });

// The claim's lifecycle as a calm k-status pill — advocacy in motion reads active,
// intake/handoff reads review, filed/withdrawn stays neutral. Never alarm-red.
const CLAIM_STATUS: Record<ClaimStatus, string> = {
  REPORTED: "pending",
  DOCUMENTING: "review",
  SUBMITTED: "review",
  IN_PROGRESS: "active",
  RESOLVED: "active",
  WITHDRAWN: "pending",
};

// AC5 — the claims desk landing: the advisor's open claims + FNOL. The
// bodily-injury → CARE consequence is surfaced at the point of filing.
export function ClaimsDesk() {
  const { t } = useT();
  const { data, isLoading, error } = useMyOpenClaims();

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("claims.heading")}</h1>
        </div>
      </header>

      {/* Care mode — the load-bearing "no upsell on this screen" statement: open-claim
          households have sales signals suppressed; the only CTAs are follow-up/updates. */}
      <div role="note" className="k-note" data-testid="claims-care-mode">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--restricted">
            <Icon name="protection" size={18} />
          </span>
          <p className="k-note__desc">{t("claims.careMode")}</p>
        </div>
      </div>

      <Fnol />

      {isLoading && (
        <div className="k-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("claims.loading")}</span>
          </div>
        </div>
      )}
      {error && (
        <div className="k-note k-note--error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <p role="alert" className="k-note__desc">
              {error instanceof ProblemError ? error.problem.detail : t("claims.error")}
            </p>
          </div>
        </div>
      )}
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="protection" size={26} />
          </div>
          <p className="k-empty__title">{t("claims.none")}</p>
        </div>
      )}

      <ul className="claims-list">
        {(data ?? []).map((c) => (
          <ClaimRow key={c.id} claim={c} />
        ))}
      </ul>
    </section>
  );
}

function ClaimRow({ claim }: { claim: Claim }) {
  const { t } = useT();
  return (
    <li
      className="k-card k-card--pad-sm claim-row"
      data-testid="claim-row"
      style={{ display: "flex", alignItems: "center", gap: 12 }}
    >
      <NavLink
        to={`/claims/${claim.id}`}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          flex: 1,
          minWidth: 0,
          textDecoration: "none",
        }}
      >
        <span style={{ fontWeight: 600, color: "var(--k-ink)" }}>{claim.lineCode}</span>
        <span style={{ fontSize: 12.5, color: "var(--k-muted)" }}>
          {claim.carrierId} · {t("claims.loss")} {fmt(claim.lossAt)}
        </span>
      </NavLink>
      <span className={`k-status k-status--${CLAIM_STATUS[claim.status] ?? "pending"}`}>
        <span className="k-status__dot" />
        {claim.status}
      </span>
      {claim.involvesBodilyInjury && (
        <span className="k-badge k-badge--warning" data-testid="injury-badge">
          <Icon name="alert" size={14} />
          {t("claims.injury")}
        </span>
      )}
      {claim.hasThirdParty && (
        <span className="k-badge k-badge--standard">{t("claims.thirdParty")}</span>
      )}
    </li>
  );
}

function Fnol() {
  const { t } = useT();
  const navigate = useNavigate();
  const fnol = useFnol();
  const [policyId, setPolicyId] = useState("");
  const [lossAt, setLossAt] = useState("");
  const [bodilyInjury, setBodilyInjury] = useState(false);
  const [thirdParty, setThirdParty] = useState(false);

  return (
    <form
      className="claims-fnol-form k-card"
      onSubmit={(e) => {
        e.preventDefault();
        if (!policyId.trim() || !lossAt) return;
        fnol.mutate(
          {
            policyId: policyId.trim(),
            lossAt: new Date(lossAt).toISOString(),
            involvesBodilyInjury: bodilyInjury,
            hasThirdParty: thirdParty,
          },
          { onSuccess: (claim) => navigate(`/claims/${claim.id}`) },
        );
      }}
    >
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("claims.fnol.heading")}
      </p>
      <label className="k-field">
        <span className="k-field__label">{t("claims.fnol.policyId")}</span>
        <input
          className="k-input"
          aria-label={t("claims.fnol.policyId")}
          value={policyId}
          onChange={(e) => setPolicyId(e.target.value)}
        />
      </label>
      <label className="k-field">
        <span className="k-field__label">{t("claims.fnol.lossAt")}</span>
        <input
          className="k-input"
          type="datetime-local"
          aria-label={t("claims.fnol.lossAt")}
          value={lossAt}
          onChange={(e) => setLossAt(e.target.value)}
        />
      </label>
      <label className="k-choice" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={bodilyInjury}
          onChange={(e) => setBodilyInjury(e.target.checked)}
        />
        <span className="k-check">
          <Icon name="check" size={14} />
        </span>
        {t("claims.fnol.bodilyInjury")}
      </label>
      <label className="k-choice" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={thirdParty}
          onChange={(e) => setThirdParty(e.target.checked)}
        />
        <span className="k-check">
          <Icon name="check" size={14} />
        </span>
        {t("claims.fnol.thirdParty")}
      </label>
      {bodilyInjury && (
        <div
          role="status"
          className="k-note k-card--accent-amber"
          data-testid="care-warning"
          style={{ marginBottom: 16 }}
        >
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--restricted">
              <Icon name="alert" size={18} />
            </span>
            <p className="k-note__desc">{t("claims.fnol.careWarning")}</p>
          </div>
        </div>
      )}
      {fnol.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {fnol.error.problem.detail}
        </p>
      )}
      <button
        type="submit"
        className="k-btn k-btn--primary"
        disabled={!policyId.trim() || !lossAt || fnol.isPending}
      >
        <Icon name="document" size={16} />
        {t("claims.fnol.file")}
      </button>
    </form>
  );
}
